import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {isMobileOnly} from 'react-device-detect';
import {LivenessAction} from './types';
import {useLivenessStore} from './store/useLivenessStore';
import {useLivenessDetection} from './hooks/useLivenessDetection';
import {useModalControls} from 'hooks';
import {useIVStore} from 'store/identityVerification';
import {IDENTITY_VERIFICATION_PHOTOS_MAX_SIZE_MB} from 'utils/constants';
import selfieGif from 'assets/img/selfie.gif?no-inline';
import {MobileWebcam} from './MobileWebcam';
import {LivenessInstructions} from './LivenessInstructions';
import {FailedModal} from 'components/IVProcessModal/FailedModal';
import {ChallengeProgressBar} from 'components/LivenessDetection/ChallengeProgressBar';
import {SpecialIndicator, Container, VideoContainer, WebcamStyled} from './styled';

const videoConstraints = {
  facingMode: 'user',
  // width: {min: 320, ideal: 1280, max: 1920},
  // height: {min: 240, ideal: 720, max: 1080},
  width: {min: 320, ideal: 640, max: 800},
  height: {min: 240, ideal: 480, max: 600},
  frameRate: {ideal: 30, max: 30},
};

const webcamProps = {
  minScreenshotW: 800,
  minScreenshotH: 600,
  screenshotMaxSize: IDENTITY_VERIFICATION_PHOTOS_MAX_SIZE_MB,
  imageSmoothing: false,
  mirrored: true,
  roundedScanArea: true,
  additionalConstraints: videoConstraints,
};

const LIVENESS_TIMEOUT = 15000;

export type LivenessDetectionProps = {
  onComplete?: (data: {
    screenshot: string | null;
    recordingVideo: Blob | null;
    performedBy: 'CLIENT' | 'SERVER';
    completedActions: LivenessAction[];
  }) => void;
  disabled?: boolean;
  className?: string;
};

export const LivenessDetection: React.FC<LivenessDetectionProps> = ({
  onComplete,
  className,
  disabled,
}) => {
  const {
    videoRef,
    isModelLoading,
    startLivenessDetection,
    faceScreenshot,
    isUsingClientDetection,
  } = useLivenessDetection();
  const {t} = useTranslation('identity-verification');
  const isCompleteRef = useRef(false);

  const {openModal, closeModal, isOpen} = useModalControls();
  const currentStep = useLivenessStore(state => state.currentStep);
  const stepStatus = useLivenessStore(state => state.stepStatus);
  const recordingVideo = useLivenessStore(state => state.recordingVideo);
  const completedActions = useLivenessStore(state => state.completedActions);
  const performedBy = isUsingClientDetection ? 'CLIENT' : 'SERVER';
  const {setIsIvFailed} = useIVStore(state => state);
  const [isFailedLivenessTriggered, setIsFailedLivenessTriggered] = useState(false);

  const shouldOpenModal = currentStep != 'COMPLETED' && !isModelLoading;

  useEffect(() => {
    if (isFailedLivenessTriggered) return;
    const timeout = setTimeout(() => {
      if (shouldOpenModal) {
        setIsFailedLivenessTriggered(true);
        openModal();
      }
    }, LIVENESS_TIMEOUT);

    return () => {
      clearTimeout(timeout);
    };
  }, [shouldOpenModal, openModal, isFailedLivenessTriggered]);

  const isComplete =
    !isCompleteRef.current &&
    currentStep === 'COMPLETED' &&
    stepStatus === 'success' &&
    onComplete &&
    !isFailedLivenessTriggered;

  useEffect(() => {
    if (isComplete) {
      onComplete({
        screenshot: faceScreenshot,
        recordingVideo,
        completedActions,
        performedBy,
      });
      isCompleteRef.current = true;
    }
  }, [
    isComplete,
    faceScreenshot,
    recordingVideo,
    onComplete,
    completedActions,
    performedBy,
  ]);

  const handleTryAgain = () => {
    setIsFailedLivenessTriggered(false);
    startLivenessDetection();
    closeModal();
    isCompleteRef.current = false;
  };

  return (
    <Container className={className}>
      <VideoContainer $status={stepStatus}>
        {!isUsingClientDetection && <SpecialIndicator />}
        {isMobileOnly ? (
          <MobileWebcam
            status={stepStatus}
            onUserMedia={startLivenessDetection}
            ref={videoRef}
            isLoading={isModelLoading}
            disabled={!!disabled}
            children={!isUsingClientDetection && <SpecialIndicator />}
            {...webcamProps}
          />
        ) : (
          <WebcamStyled
            onUserMedia={startLivenessDetection}
            ref={videoRef}
            isLoading={isModelLoading}
            {...webcamProps}
          >
            <ChallengeProgressBar />
          </WebcamStyled>
        )}
      </VideoContainer>

      <LivenessInstructions />
      {isOpen && (
        <FailedModal
          open={isOpen}
          reasons={[t('liveness_detection.detection_failed_reasons')]}
          title={t('liveness_detection.detection_failed')}
          onTryAgain={handleTryAgain}
          onClose={closeModal}
          image={selfieGif}
          onVerifyLater={() => setIsIvFailed(true)}
        />
      )}
    </Container>
  );
};
